/**
 * AdminDataSourcesTab Component
 *
 * Admin interface for managing MongoDB servers and databases.
 * Only visible to admin users.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Database } from 'lucide-react';
import {
  serverApi,
  MongoServer,
  DatabaseConnection,
  ServerWithDatabases,
} from '@/services/api/serverApi';
import { ServerCard } from './ServerCard';
import { AddServerDialog } from './AddServerDialog';
import { CreateDatabaseDialog } from './CreateDatabaseDialog';
import * as errorUtils from '@/utils/errors';

interface ServerData {
  server: MongoServer;
  databases: DatabaseConnection[];
}

export const AdminDataSourcesTab: React.FC = () => {
  const [servers, setServers] = useState<ServerData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [showAddServer, setShowAddServer] = useState(false);
  const [showCreateDatabase, setShowCreateDatabase] = useState(false);
  const [selectedServerForDb, setSelectedServerForDb] = useState<MongoServer | null>(null);
  const [testingServerId, setTestingServerId] = useState<string | null>(null);

  // Fetch servers with their databases
  const fetchServers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const serverList = await serverApi.listServers();

      // Fetch databases for each server
      const serversWithDatabases: ServerData[] = await Promise.all(
        serverList.map(async (server) => {
          try {
            const { databases } = await serverApi.getServer(server.id);
            return { server, databases };
          } catch {
            return { server, databases: [] };
          }
        })
      );

      setServers(serversWithDatabases);
    } catch (err: unknown) {
      // Handle API error object { code, message, details } or Error instance
      const errorMessage = errorUtils.getErrorMessageOrFallback(err, 'Failed to load servers');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  // Handle test connection
  const handleTestConnection = useCallback(async (server: MongoServer) => {
    setTestingServerId(server.id);

    try {
      const result = await serverApi.testServer(server.id);

      // Update local state with test result
      setServers((prev) =>
        prev.map((s) =>
          s.server.id === server.id
            ? {
                ...s,
                server: {
                  ...s.server,
                  health: {
                    ...s.server.health,
                    status: result.success ? 'healthy' : 'unhealthy',
                    lastCheck: new Date().toISOString(),
                    latencyMs: result.latencyMs,
                    version: result.serverInfo?.version,
                    errorMessage: result.error,
                  },
                },
              }
            : s
        )
      );
    } catch (err) {
      console.error('Failed to test connection:', err);
    } finally {
      setTestingServerId(null);
    }
  }, []);

  // Handle edit server
  const handleEditServer = useCallback((server: MongoServer) => {
    // For now, we'll just log - can implement edit dialog later
    console.log('Edit server:', server);
    // TODO: Implement edit server dialog
  }, []);

  // Handle delete server
  const handleDeleteServer = useCallback(
    async (server: MongoServer) => {
      if (!window.confirm(`Are you sure you want to delete "${server.name}"? This action cannot be undone.`)) {
        return;
      }

      try {
        await serverApi.deleteServer(server.id);
        await fetchServers();
      } catch (err: unknown) {
        const errorMessage = errorUtils.getErrorMessageOrFallback(err, 'Failed to delete server');
        alert(errorMessage);
      }
    },
    [fetchServers]
  );

  // Handle create database
  const handleCreateDatabase = useCallback((server: MongoServer) => {
    setSelectedServerForDb(server);
    setShowCreateDatabase(true);
  }, []);

  // Handle dialog success
  const handleAddServerSuccess = useCallback(() => {
    fetchServers();
  }, [fetchServers]);

  const handleCreateDatabaseSuccess = useCallback(() => {
    fetchServers();
    setSelectedServerForDb(null);
  }, [fetchServers]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">MongoDB Servers</h2>
            <p className="text-sm text-muted-foreground">
              Register MongoDB servers and create databases for your data sources.
            </p>
          </div>
          <Button
            onClick={() => setShowAddServer(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            + Add Server
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <div className="text-muted-foreground">Loading servers...</div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="p-4 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <div className="font-medium">Error loading servers</div>
            <div className="text-sm mt-1">{error}</div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={fetchServers}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && servers.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <Database className="h-12 w-12 text-muted-foreground mb-4" />
            <div className="text-lg font-medium">No servers registered</div>
            <div className="text-sm text-muted-foreground mb-4">
              Add a MongoDB server to start managing your databases.
            </div>
            <Button
              onClick={() => setShowAddServer(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              + Add Your First Server
            </Button>
          </div>
        )}

        {/* Server list */}
        {!isLoading && !error && servers.length > 0 && (
          <div className="space-y-4">
            {servers.map(({ server, databases }) => (
              <ServerCard
                key={server.id}
                server={server}
                databases={databases}
                onTest={handleTestConnection}
                onEdit={handleEditServer}
                onDelete={handleDeleteServer}
                onCreateDatabase={handleCreateDatabase}
                isTestingConnection={testingServerId === server.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddServerDialog
        open={showAddServer}
        onOpenChange={setShowAddServer}
        onSuccess={handleAddServerSuccess}
      />

      <CreateDatabaseDialog
        open={showCreateDatabase}
        onOpenChange={setShowCreateDatabase}
        server={selectedServerForDb}
        onSuccess={handleCreateDatabaseSuccess}
      />
    </div>
  );
};

export default AdminDataSourcesTab;
